import React from "react";
import { createPortal } from "react-dom";
import { marked } from "marked";
import PerFeatureAnalysisView, {
  FeatureColumnData,
} from "components/PerFeatureAnalysisView";
import ExpandableZoomableImage from "components/ExpandableZoomableImage";
import ZoomableImage from "components/ZoomableImage";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { getMarkdownContent } from "services/project";
import {
  pfBuildEmphParts,
  pfBuildOwnChartHtml,
  pfCellHtml,
  pfEmphFig,
  pfEscapeHtml,
  pfExtractAccusedUrl,
  pfExtractClause,
  pfExtractFigureInsight,
  pfHex,
  pfNorm,
  pfNormFig,
  pfOwnChartRows,
  pfParseChart,
} from "utils/pfMarkdown";

interface StripItem {
  it: any;
  idx: number;
  isMatched: boolean;
}

interface ClauseFeatureStripProps {
  items: StripItem[];
  comparisonId: string | null;
  patentId: string;
  claimKey: string;
  /** Claim object of the claim view — legend fallback, keyed FIG_x. */
  claim: any;
  /** patent_dict — analyzer-built element profiles for the figure modal. */
  patentDict?: any;
  targetLink?: string;
  /** Opens the full View More Details page for a column's item (annotation /
   * reanalyze / change-image live there). preferredView picks the page's
   * initial mode — "classic" for the chart expand, default "graph". */
  onViewMore: (
    it: any,
    idx: number,
    isMatched: boolean,
    preferredView?: "graph" | "classic"
  ) => void;
  /** Opens the citations sidebar for a clicked [n] citation number. */
  onCitationClick?: (num: string) => void;
}

// [n] / [n,m] citation tokens → clickable sups — same conversion the View More
// page's walker effect does, but at HTML-string level.
const citeHtml = (html: string) =>
  (html || "").replace(
    /\[\[(\d+(?:\s*,\s*\d+)*)\]\]|\[(\d+(?:\s*,\s*\d+)*)\]/g,
    (_m, a, b) =>
      (a || b)
        .split(",")
        .map((n: string) => n.trim())
        .sort((x: string, y: string) => Number(x) - Number(y))
        .map(
          (n: string, i: number, arr: string[]) =>
            `<sup><span class="citation-link" data-citation-number="${n}" style="color:#1751D0;cursor:pointer;">${n}</span></sup>${
              i < arr.length - 1 ? "<sup>,</sup>" : ""
            }`
        )
        .join("")
  );

// Presigned sibling data survives clause collapse/expand and target toggling.
const stripCache: Record<string, any> = {};

/**
 * Inline per-clause feature strip for the claim view's Features View — the
 * same columns (patent figure vs accused image, charts, analysis) as the
 * View More Details page, assembled from each item's own stored markdown.
 */
const ClauseFeatureStrip: React.FC<ClauseFeatureStripProps> = ({
  items,
  comparisonId,
  patentId,
  claimKey,
  claim,
  patentDict,
  targetLink,
  onViewMore,
  onCitationClick,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [columns, setColumns] = React.useState<FeatureColumnData[]>([]);
  const [clauseHtml, setClauseHtml] = React.useState("");
  // Structured common chart so the Patent Figure / Accused Image columns can
  // render React image components (zoom + expand + rotate) per row.
  const [commonChart, setCommonChart] = React.useState<{
    header: string[];
    rows: { colKey: string; cellsHtml: string[] }[];
  } | null>(null);
  const [currentKey, setCurrentKey] = React.useState<string | null>(null);
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);
  const colItemsRef = React.useRef<Record<string, StripItem>>({});
  // Hover preview + click modal for boxed feature mentions in the chart
  const [hoverPreview, setHoverPreview] = React.useState<{
    key: string;
    left: number;
    top: number;
    above: boolean;
  } | null>(null);
  const [figureModalKey, setFigureModalKey] = React.useState<string | null>(
    null
  );

  React.useEffect(() => {
    const clear = () => setHoverPreview(null);
    window.addEventListener("scroll", clear, true);
    return () => window.removeEventListener("scroll", clear, true);
  }, []);

  React.useEffect(() => {
    if (!figureModalKey) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setFigureModalKey(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [figureModalKey]);

  // Same matching as ClaimElement.profilesFor: normalized patent_dict keys
  // matched against the feature text, keeping only the most specific.
  const profilesForFeature = (description: string) => {
    if (!patentDict || typeof patentDict !== "object") return [];
    const norm = (s: string) =>
      String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const normDesc = norm(description);
    if (!normDesc) return [];
    const matched = Object.entries(patentDict)
      .filter(
        ([key, value]) =>
          typeof value === "string" &&
          (value as string).trim() &&
          norm(key) &&
          normDesc.includes(norm(key))
      )
      .sort(([a], [b]) => norm(b).length - norm(a).length);
    const kept: [string, any][] = [];
    matched.forEach(([key, value]) => {
      if (!kept.some(([k]) => norm(k).includes(norm(key))))
        kept.push([key, value]);
    });
    return kept.map(([key, value]) => ({
      name: key.replace(/_/g, " "),
      text: String(value).trim().replace(/^"|"$/g, ""),
    }));
  };

  // Figure → legend rows from the claim structure (items' own figure_legend
  // arrays are empty in practice — the real legends live on the claim).
  const claimLegendMap = React.useMemo(() => {
    const map: Record<string, { [k: string]: string }[]> = {};
    const add = (legends: any) => {
      (Array.isArray(legends) ? legends : []).forEach((l: any) => {
        const k = Object.keys(l || {})[0];
        if (k && Array.isArray(l[k]) && !map[pfNormFig(k)]) {
          map[pfNormFig(k)] = l[k];
        }
      });
    };
    add(claim?.legends);
    (claim?.elements || []).forEach((el: any) => {
      add(el?.legends);
      (el?.features || []).forEach((f: any) => add(f?.legends));
    });
    return map;
  }, [claim]);

  // Parent re-renders rebuild the items array each time — key the effect on a
  // stable signature so it only refetches when the actual items change.
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const itemsSig = items
    .map((e) => e.it?.annotation_id || `${e.isMatched ? "m" : "n"}-${e.idx}`)
    .join("|");

  React.useEffect(() => {
    let cancelled = false;
    const usable = itemsRef.current.filter(
      (e) =>
        e.it &&
        typeof e.it === "object" &&
        e.it.sub_feature &&
        !e.it.is_design_patent
    );
    if (usable.length === 0) {
      setColumns([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    (async () => {
      // Each column pulls that item's OWN stored analysis; the markdown
      // endpoint presigns its patent figure + markdown URLs.
      const enriched = await Promise.all(
        usable.map(async (e) => {
          let md: string = e.it.markdown_content || "";
          let figureUrl = "";
          let figureNumber = e.it.patent_image?.figure_number || "";
          let figureLegend = e.it.patent_image?.figure_legend || [];
          if (e.it.annotation_id && comparisonId) {
            try {
              let data = stripCache[e.it.annotation_id];
              if (!data) {
                const res: any = await getMarkdownContent(
                  comparisonId,
                  patentId,
                  claimKey,
                  e.isMatched,
                  e.it.annotation_id
                );
                if (res?.success && res?.data) {
                  data = res.data;
                  stripCache[e.it.annotation_id] = data;
                }
              }
              if (data) {
                md = data.markdown_content || md;
                figureUrl = data.patentImage?.patent_figure_path || "";
                figureNumber = data.patentImage?.figure_number || figureNumber;
                figureLegend = data.patentImage?.figure_legend || figureLegend;
              }
            } catch (err) {
              console.error("Error fetching clause feature data:", err);
            }
          }
          if (
            !figureUrl &&
            typeof e.it.patent_image?.patent_figure_path === "string" &&
            e.it.patent_image.patent_figure_path.startsWith("https://")
          ) {
            figureUrl = e.it.patent_image.patent_figure_path;
          }
          return { ...e, md, figureUrl, figureNumber, figureLegend };
        })
      );
      if (cancelled) return;

      const clause = pfExtractClause(enriched.find((x) => x.md)?.md || "");
      const clausePos = (sf: string) => {
        const i = clause.toLowerCase().indexOf(String(sf || "").toLowerCase());
        return i < 0 ? Number.MAX_SAFE_INTEGER : i;
      };
      enriched.sort(
        (a, b) => clausePos(a.it.sub_feature) - clausePos(b.it.sub_feature)
      );

      const emphParts = pfBuildEmphParts(clause);
      const keyOf = (e: StripItem) =>
        e.it.annotation_id || `${e.isMatched ? "m" : "n"}-${e.idx}`;

      const legendFor = (itemLegend: any, figureNumber: any) =>
        Array.isArray(itemLegend) && itemLegend.length > 0
          ? itemLegend
          : claimLegendMap[pfNormFig(figureNumber)];

      colItemsRef.current = {};
      const cols: FeatureColumnData[] = enriched.map((e) => {
        const key = keyOf(e);
        colItemsRef.current[key] = e;
        let analysisHtml = "";
        try {
          analysisHtml = marked.parse(
            pfEmphFig(emphParts(pfEscapeHtml(String(e.it.text || ""))))
          ) as string;
        } catch {}
        let figureInsightHtml = "";
        const fi = pfExtractFigureInsight(e.md);
        if (fi) {
          try {
            figureInsightHtml = marked.parse(pfEmphFig(emphParts(fi))) as string;
          } catch {}
        }
        return {
          key,
          subFeature: String(e.it.sub_feature),
          isMatched: e.isMatched,
          isCurrent: false,
          hexCode: pfHex(e.it.element_hex_code) || undefined,
          patentFigureUrl: e.figureUrl || undefined,
          figureNumber: e.figureNumber || undefined,
          figureLegend: legendFor(e.figureLegend, e.figureNumber),
          accusedImageUrl: pfExtractAccusedUrl(e.md) || undefined,
          chartHtml:
            citeHtml(pfBuildOwnChartHtml(e.md, e.it.sub_feature, emphParts)) ||
            undefined,
          analysisHtml: citeHtml(analysisHtml) || undefined,
          figureInsightHtml: citeHtml(figureInsightHtml) || undefined,
          matchPercentage: e.it.match_percentage,
        };
      });

      // Common claim chart: ONE table for the whole clause — each feature's
      // row(s) pulled from that feature's OWN item's chart. Structured (not
      // HTML) so image columns can be injected at render time.
      let common: {
        header: string[];
        rows: { colKey: string; cellsHtml: string[] }[];
      } | null = null;
      const headerSource = enriched.find((e) => pfParseChart(e.md));
      const commonHeader = headerSource
        ? pfParseChart(headerSource.md)!.header
        : null;
      if (commonHeader) {
        const analysisIdx = commonHeader.findIndex((h) => /analysis/i.test(h));
        const rowsOut: { colKey: string; cellsHtml: string[] }[] = [];
        enriched.forEach((e) => {
          const chart = pfParseChart(e.md);
          let rows = chart ? pfOwnChartRows(chart, e.it.sub_feature) : [];
          if (rows.length === 0) {
            rows = [
              [
                String(e.it.sub_feature),
                ...Array(Math.max(commonHeader.length - 1, 0)).fill("—"),
              ],
            ];
          }
          const insight = pfExtractFigureInsight(e.md);
          rows.forEach((r) => {
            rowsOut.push({
              colKey: keyOf(e),
              cellsHtml: Array.from({ length: commonHeader.length }, (_, ci) =>
                citeHtml(
                  pfCellHtml(
                    ci === analysisIdx && insight ? insight : r[ci] ?? "—",
                    emphParts
                  )
                )
              ),
            });
          });
        });
        common = { header: commonHeader, rows: rowsOut };
      }

      setColumns(cols);
      setClauseHtml("");
      setCommonChart(common);
      setCurrentKey(cols[0]?.key || null);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsSig, comparisonId, patentId, claimKey, claimLegendMap]);

  const openViewMoreFor = (key: string, view?: "graph" | "classic") => {
    const e = colItemsRef.current[key];
    if (e) onViewMore(e.it, e.idx, e.isMatched, view);
  };

  // Color boxed feature chips (.fig-ref) with their feature's hex — same
  // per-feature coding as the pills. Runs after every render (cells are
  // dangerouslySetInnerHTML, recreated on re-render); idempotent and cheap.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root || columns.length === 0) return;
    const partNum = (s: string) =>
      ((s || "").match(/\((\s*\d+\s*)\)/) || [])[1]?.replace(/\s/g, "") || "";
    const nameNorm = (s: string) =>
      pfNorm(String(s || "").replace(/\(\s*\d+\s*\)/g, ""));
    root.querySelectorAll<HTMLElement>(".fig-ref").forEach((chip) => {
      const text = chip.textContent || "";
      const num = partNum(text);
      const col =
        (num
          ? columns.find((c) => partNum(c.subFeature) === num)
          : undefined) ||
        columns.find((c) => pfNorm(c.subFeature) === pfNorm(text)) ||
        columns.find(
          (c) =>
            !!nameNorm(c.subFeature) &&
            nameNorm(c.subFeature) === nameNorm(text)
        );
      const hex = col?.hexCode;
      if (hex) {
        chip.style.color = hex;
        chip.style.background = `${hex}14`;
        chip.style.borderColor = `${hex}55`;
      }
    });

    // Feature / Patent Element cells: color raw occurrences of each
    // sub-feature name (unnumbered or list-numbered ones have no chip).
    const bySize = columns
      .filter((c) => c.hexCode)
      .sort((a, b) => b.subFeature.length - a.subFeature.length);
    root.querySelectorAll<HTMLElement>("[data-feat-cell]").forEach((cell) => {
      bySize.forEach((c) => {
        const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
        const nodes: Text[] = [];
        while (walker.nextNode()) {
          const t = walker.currentNode as Text;
          const p = t.parentElement;
          if (p && (p.closest(".fig-ref") || p.closest("[data-feat-colored]")))
            continue;
          nodes.push(t);
        }
        nodes.forEach((t) => {
          const text = t.textContent || "";
          const idx = text.indexOf(c.subFeature);
          if (idx < 0) return;
          const span = document.createElement("span");
          span.setAttribute("data-feat-colored", "1");
          span.style.color = c.hexCode!;
          span.style.fontWeight = "600";
          span.textContent = c.subFeature;
          const after = t.splitText(idx);
          after.textContent = text.slice(idx + c.subFeature.length);
          t.parentNode?.insertBefore(span, after);
        });
      });
    });
  });

  // A rotate-and-save keeps the S3 key but the browser caches the old
  // presigned URL — swap in the fresh one wherever the old one appears.
  const handleImageRotated = (newUrl: string, oldUrl: string) => {
    setColumns((prev) =>
      prev.map((c) =>
        c.patentFigureUrl === oldUrl || c.accusedImageUrl === oldUrl
          ? {
              ...c,
              patentFigureUrl:
                c.patentFigureUrl === oldUrl ? newUrl : c.patentFigureUrl,
              accusedImageUrl:
                c.accusedImageUrl === oldUrl ? newUrl : c.accusedImageUrl,
            }
          : c
      )
    );
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-4 mb-4">
        {/* Spinner always at viewport center, same spot as page loads */}
        <div className="fixed inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <div className="flex gap-4 animate-pulse">
          <div className="h-64 bg-gray-200 rounded flex-1"></div>
          <div className="h-64 bg-gray-200 rounded flex-1"></div>
        </div>
      </div>
    );
  }
  if (columns.length === 0) return null;

  // Boxed feature mentions (.fig-ref chips) in the chart: hover previews that
  // feature's patent figure, click opens the full figure modal — same manner
  // as the View More page's claim chart.
  const chipPartNum = (s: string) =>
    ((s || "").match(/\((\s*\d+\s*)\)/) || [])[1]?.replace(/\s/g, "") || "";
  const chipNameNorm = (s: string) =>
    pfNorm(String(s || "").replace(/\(\s*\d+\s*\)/g, ""));
  const chipColFor = (el: HTMLElement) => {
    const chip = el.closest(".fig-ref") as HTMLElement | null;
    if (!chip) return null;
    const text = chip.textContent || "";
    const num = chipPartNum(text);
    return {
      span: chip,
      col:
        (num
          ? columns.find((c) => chipPartNum(c.subFeature) === num)
          : undefined) ||
        columns.find((c) => pfNorm(c.subFeature) === pfNorm(text)) ||
        columns.find(
          (c) =>
            !!chipNameNorm(c.subFeature) &&
            chipNameNorm(c.subFeature) === chipNameNorm(text)
        ),
    };
  };

  const handleMouseOver = (ev: React.MouseEvent) => {
    const t = ev.target;
    if (!(t instanceof HTMLElement)) return;
    const hit = chipColFor(t);
    if (!hit?.col?.patentFigureUrl) {
      setHoverPreview(null);
      return;
    }
    hit.span.style.cursor = "zoom-in";
    const rect = hit.span.getBoundingClientRect();
    const half = 140; // popup is 280px wide — keep it inside the viewport
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, half + 8),
      window.innerWidth - half - 8
    );
    const above = rect.bottom + 300 > window.innerHeight;
    const top = above ? rect.top - 8 : rect.bottom + 8;
    const key = hit.col.key;
    setHoverPreview((prev) =>
      prev &&
      prev.key === key &&
      prev.left === left &&
      prev.top === top &&
      prev.above === above
        ? prev
        : { key, left, top, above }
    );
  };

  // The citation sups live inside dangerouslySetInnerHTML — delegate clicks.
  const handleClick = (ev: React.MouseEvent) => {
    const t = ev.target as HTMLElement;
    if (t.classList?.contains("citation-link") && t.dataset.citationNumber) {
      onCitationClick?.(t.dataset.citationNumber);
      return;
    }
    const hit = chipColFor(t);
    if (hit?.col?.patentFigureUrl) {
      ev.preventDefault();
      setHoverPreview(null);
      setFigureModalKey(hit.col.key);
    }
  };

  const hoverCol = hoverPreview
    ? columns.find((c) => c.key === hoverPreview.key)
    : undefined;
  const figureModalCol = figureModalKey
    ? columns.find((c) => c.key === figureModalKey)
    : undefined;
  const figureModalProfiles = figureModalCol
    ? profilesForFeature(figureModalCol.subFeature)
    : [];

  // Same column placement as the View More classic chart: "Patent Figure"
  // beside "Patent Element", "Accused Image" beside "Accused Instrumentality".
  const colByKey = (key: string) => columns.find((c) => c.key === key);
  const patentIdx = commonChart
    ? commonChart.header.findIndex((h) => /patent\s*element/i.test(h))
    : -1;
  const accusedIdx = commonChart
    ? commonChart.header.findIndex((h) => /accused\s*instrumentality/i.test(h))
    : -1;
  const anyPatentImg = columns.some((c) => c.patentFigureUrl);
  const anyAccusedImg = columns.some((c) => c.accusedImageUrl);
  const showPatentCol = patentIdx >= 0 && anyPatentImg;
  const showAccusedCol = accusedIdx >= 0 && anyAccusedImg;
  const imgCellStyle: React.CSSProperties = {
    minWidth: "14rem",
    verticalAlign: "top",
  };

  // Numbered "10. ear" list below the figure — keeps the column narrow; the
  // expanded overlay still shows image + legend side by side.
  const legendListEl = (legend: any) =>
    Array.isArray(legend) && legend.length > 0 ? (
      <ul className="mt-2 pt-2 border-t border-dashed border-gray-200 max-h-40 overflow-y-auto space-y-1 text-left [scrollbar-width:thin]">
        {legend.map((lg: any, li: number) => {
          const k = Object.keys(lg || {})[0];
          if (!k) return null;
          return (
            <li key={li} className="text-[11.5px] leading-snug text-gray-800">
              {k}. {lg[k]}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div
      ref={rootRef}
      className="mb-4"
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseLeave={() => setHoverPreview(null)}
    >
      <PerFeatureAnalysisView
        claimLabel=""
        clauseHtml={clauseHtml}
        columns={columns.map((c) => ({ ...c, isCurrent: c.key === currentKey }))}
        expandedKey={expandedKey}
        onToggleExpand={setExpandedKey}
        onSelectColumn={setCurrentKey}
        onChangePatentImage={openViewMoreFor}
        onAnnotate={openViewMoreFor}
        onReanalyze={openViewMoreFor}
        targetLink={targetLink}
        onImageRotated={handleImageRotated}
      />

      {/* Common claim chart with image columns — zoom/expand/rotate per row */}
      {commonChart && (
        <div className="mt-7">
          <div className="flex items-center gap-2 mb-1.5">
            <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085]">
              Claim Chart — All Features
            </p>
            <button
              type="button"
              title="View More Details"
              onClick={() =>
                openViewMoreFor(currentKey || columns[0]?.key || "", "classic")
              }
              className="p-1 rounded border border-gray-200 bg-white shadow-sm hover:bg-gray-100 text-[#667085]"
              style={{ cursor: "pointer" }}
            >
              <OpenInFullIcon sx={{ fontSize: 14 }} />
            </button>
          </div>
          <div className="feature-chart feature-common-chart overflow-x-auto">
            <table>
              <thead>
                <tr>
                  {commonChart.header.map((h, hi) => (
                    <React.Fragment key={hi}>
                      <th dangerouslySetInnerHTML={{ __html: pfEscapeHtml(h) }} />
                      {hi === patentIdx && showPatentCol && (
                        <th style={{ minWidth: "14rem" }}>Patent Figure</th>
                      )}
                      {hi === accusedIdx && showAccusedCol && (
                        <th style={{ minWidth: "14rem" }}>Accused Image</th>
                      )}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commonChart.rows.map((row, ri) => {
                  const col = colByKey(row.colKey);
                  return (
                    <tr
                      key={ri}
                      className={row.colKey === currentKey ? "pf-current" : undefined}
                    >
                      {row.cellsHtml.map((h, ci) => (
                        <React.Fragment key={ci}>
                          <td
                            style={{ verticalAlign: "top" }}
                            {...(ci === 0 || ci === patentIdx
                              ? { "data-feat-cell": "1" }
                              : {})}
                            dangerouslySetInnerHTML={{ __html: h }}
                          />
                          {ci === patentIdx && showPatentCol && (
                            <td style={imgCellStyle}>
                              {col?.patentFigureUrl ? (
                                <>
                                  <ExpandableZoomableImage
                                    url={col.patentFigureUrl}
                                    alt={`Patent figure — ${col.subFeature}`}
                                    title={`Patent Figure — ${col.subFeature}`}
                                    height="13rem"
                                    legend={col.figureLegend}
                                    onRotated={handleImageRotated}
                                  />
                                  {legendListEl(col.figureLegend)}
                                </>
                              ) : null}
                            </td>
                          )}
                          {ci === accusedIdx && showAccusedCol && (
                            <td style={imgCellStyle}>
                              {col?.accusedImageUrl ? (
                                <ExpandableZoomableImage
                                  url={col.accusedImageUrl}
                                  alt={`Accused instrumentality — ${col.subFeature}`}
                                  title={`Accused Instrumentality — ${col.subFeature}`}
                                  height="13rem"
                                  link={targetLink}
                                  linkLabel="View Target"
                                  onRotated={handleImageRotated}
                                />
                              ) : null}
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Patent-figure preview for the hovered feature mention */}
      {hoverPreview &&
        hoverCol?.patentFigureUrl &&
        createPortal(
          <div
            className="fixed pointer-events-none"
            style={{
              left: hoverPreview.left,
              top: hoverPreview.top,
              transform: `translate(-50%, ${
                hoverPreview.above ? "-100%" : "0"
              })`,
              zIndex: 1300,
            }}
          >
            <div className="w-[280px] bg-white border border-gray-200 rounded-lg shadow-xl p-2">
              <img
                src={hoverCol.patentFigureUrl}
                alt={hoverCol.subFeature}
                className="w-full max-h-[220px] object-contain bg-[#FAFBFC] rounded"
              />
              <p className="mt-1.5 text-center text-[11px] font-interSemiBold truncate">
                <span
                  style={hoverCol.hexCode ? { color: hoverCol.hexCode } : undefined}
                >
                  {hoverCol.subFeature}
                </span>
                {hoverCol.figureNumber && (
                  <span className="text-[#667085] font-interMedium">
                    {" · "}
                    {hoverCol.figureNumber.replace(/_/g, ". ")}
                  </span>
                )}
              </p>
            </div>
          </div>,
          document.body
        )}

      {/* Full figure modal for a clicked feature mention */}
      {figureModalCol?.patentFigureUrl &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 1250 }}
            onClick={() => setFigureModalKey(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
              style={{ width: "70vw", maxWidth: "980px", maxHeight: "82vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
                <span className="text-[14px] font-interSemiBold">
                  <span
                    style={
                      figureModalCol.hexCode
                        ? { color: figureModalCol.hexCode }
                        : { color: "#1A202C" }
                    }
                  >
                    {figureModalCol.subFeature}
                  </span>
                  {figureModalCol.figureNumber && (
                    <span className="text-[#667085] font-interMedium">
                      {" · "}
                      {figureModalCol.figureNumber.replace(/_/g, ". ")}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  title="Close"
                  onClick={() => setFigureModalKey(null)}
                  className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                  style={{ cursor: "pointer" }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0 bg-[#FAFBFC] rounded-md overflow-hidden">
                    <ZoomableImage
                      url={figureModalCol.patentFigureUrl}
                      alt={figureModalCol.subFeature}
                      height={figureModalProfiles.length > 0 ? "56vh" : "66vh"}
                    />
                  </div>
                  {figureModalCol.figureLegend &&
                    figureModalCol.figureLegend.length > 0 && (
                      <ul className="w-64 flex-shrink-0 overflow-y-auto space-y-1.5 pl-4 border-l border-dashed border-gray-200 text-left [scrollbar-width:thin]">
                        {figureModalCol.figureLegend.map((legend, index) => {
                          const k = Object.keys(legend)[0];
                          return (
                            <li
                              key={index}
                              className="text-[13px] leading-snug text-gray-800"
                            >
                              {k}. {legend[k]}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                </div>

                {/* Analyzer-built element profile(s) */}
                {figureModalProfiles.length > 0 && (
                  <div className="mt-4 border border-gray-200 rounded-lg bg-[#F8FAFC] px-4 py-3">
                    <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-2">
                      Element Profile
                    </p>
                    <div className="space-y-3">
                      {figureModalProfiles.map((profile) => (
                        <div key={profile.name}>
                          <p className="text-sm font-interSemiBold text-[#1A202C] capitalize">
                            {profile.name}
                          </p>
                          <p className="text-sm text-[#475569] leading-relaxed">
                            {profile.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ClauseFeatureStrip;
